import { Component, Input, Output, EventEmitter, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AQAService } from '../../services/aqa.service';
import { AQADocument } from '../../models/aqa-document.interface';
import { AISearchResultItem, AQASearchResult } from '../../models';
import { formatFileSize, getAttachmentIcon } from '../../aqa.utils';
import { AqaThreadComponent } from '../aqa-thread/aqa-thread.component';
import { catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { LoadingIndicatorComponent } from '../../../core/components';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';


export interface EmailHighlights {
  content?: string[];
  subject?: string[];
  attachment?: string[];
}

@Component({
  selector: 'app-aqa-viewer',
  standalone: true,
  imports: [CommonModule, AqaThreadComponent, LoadingIndicatorComponent],
  templateUrl: './aqa-viewer.component.html',
  styleUrl: './aqa-viewer.component.scss'
})
export class AqaViewerComponent implements OnInit {
  private readonly aqaService = inject(AQAService);
  private id: string | null = null;

  //@Input({ required: true }) email!: AISearchResultItem;
  @Input({ required: true }) set email(value: AQASearchResult) {
    this.setEmailData(value);
  }

  @Input() highlights?: EmailHighlights;
  @Output() closed = new EventEmitter<void>();

  showThread = signal(false);
  threadEmails = signal<AQADocument[]>([]);
  loading = true;
  loadingMessage: string = "Loading...";

  emailData: AQADocument = { id: "" };
  protected safeHtml!: SafeHtml;

  constructor(
    private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
  }

  setEmailData(value: AQASearchResult) {
    this.id = value.document.id;
    this.loading = true;
    this.aqaService.getEmail(value.document.chunk_id!).pipe(
      catchError((error) => {
        this.loading = false;
        this.closed.emit();
        return EMPTY;
      })
    ).subscribe((response) => {
      this.loading = false;
      this.emailData = response;

      if (this.emailData.message_text) {
        this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(this.emailData.message_text);
      }

      this.loadThread();
    }
    );
  }

  close(): void {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('viewer-backdrop')) {
      this.close();
    }
  }

  attachmentClicked(name: string) {
    this.loading = true;

    if (this.emailData.metadata_storage_path == undefined || this.emailData.metadata_storage_path == null || this.emailData.metadata_storage_path == "") {
      console.error('File not found');
      this.loading = false;
      return;
    }

    this.aqaService.getAttachmentByStoragePath(this.emailData.metadata_storage_path!, name).pipe(
      catchError((error) => {
        console.error('Error fetching document:', error);
        this.loading = false;
        return EMPTY;
      })
    ).subscribe((response) => {
      this.loading = false;

      if (response.body) {
        const blob = response.body;
        const blobUrl = URL.createObjectURL(blob);

        // Open in new tab for viewable types, download for others
        const viewableTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'text/plain', 'text/html'];
        const contentType = response.headers.get('Content-Type') || blob.type;

        if (viewableTypes.some(type => contentType.includes(type))) {
          window.open(blobUrl, '_blank');
        } else {
          // Trigger download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = name || 'document';
          link.click();
          URL.revokeObjectURL(blobUrl);
        }
      }
    })
  }

  threadClicked(thread: AQADocument) {
    var doc: AQASearchResult = {
      document: thread
    };
    this.setEmailData(doc);
  }

  toggleThread(): void {
    this.showThread.update(v => !v);
  }

  loadThread(): void {
    if (!this.emailData.threadId) return;

    this.aqaService.getThread(this.emailData.threadId).subscribe({
      next: (response) => this.threadEmails.set(response.emails),
      error: () => this.threadEmails.set([])
    });
  }

  formatDateTime(dateString?: string): string {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getSize(bytes?: number): string {
    return formatFileSize(bytes);
  }

  getExtension(ext: string): string {
    var nameSplit = ext.split(".");
    return nameSplit[nameSplit.length-1];
  }

  getIcon(ext?: string): string {
    return getAttachmentIcon(ext);
  }
}
